<template>
  <div id="completeYourRegistration">
    <!-- Logo area -->
    <el-row
      type="flex"
      class="logoWrap"
      :class="`${$mq}Mode`"
    >
      <el-col
        class="logoCol"
        :span="responsiveCol"
      >
        <div
          class="logo"
          @click="$router.push({name: 'login'}).catch((err) => {})"
        >
          <img
            src="@/assets/logo/gcg_logo.png"
            alt="logo"
          />
        </div>
      </el-col>

      <el-col
        v-if="$mq !== 'mobile'"
        class="sloganCol"
        :span="responsiveCol"
      >
        <div class="slogan">
          <img
            src="@/assets/logo/gcg_slogan.png"
            alt="logo"
          />
        </div>
      </el-col>
    </el-row>

    <el-card :body-style="{padding: '50px 0'}">
      <div class="title">
        {{ $t('demographics.asStart') }}
      </div>

      <div class="subTitle">
        {{ $t('demographics.completeBelowCoach') }}
      </div>

      <el-divider />

      <el-form
        ref="form"
        :model="response"
        :rules="rules"
      >
        <div class="question">
          1. {{ $t('demographics.highestDegree.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="highest_degree">
            <el-select
              v-model="response.highest_degree"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
            >
              <el-option
                v-for="item in degreesList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          2. {{ $t('demographics.degree.question') }}*
        </div>

        <div class="response degrees">
          <el-row
            v-for="(item, key) in response.degree_list"
            :key="key"
            class="degreeItem"
            :gutter="20"
          >
            <el-col
              :span="6"
              class="label"
            >
              <div>{{ $t('demographics.degree.label') }}</div>
            </el-col>
            <el-col :span="9">
              <div>
                <el-select
                  v-model="response.degree_list[key].degree"
                  :placeholder="$t('demographics.pleaseSelect')"
                  :loading="processLoadingList.length > 0"
                >
                  <el-option
                    v-for="it in degreesList"
                    :key="it.key"
                    :label="it.value"
                    :value="it.key"
                  />
                </el-select>
              </div>
            </el-col>
            <el-col :span="9">
              <div>
                <el-select
                  v-model="response.degree_list[key].major"
                  :placeholder="$t('demographics.pleaseSelect')"
                  :loading="processLoadingList.length > 0"
                >
                  <el-option
                    v-for="it in degreeMajorList"
                    :key="it.key"
                    :label="it.value"
                    :value="it.key"
                  />
                </el-select>
              </div>
            </el-col>

            <el-input
              v-if="response.degree_list[key].major === 'Other'"
              v-model="response.degree_list[key].other"
              class="otherInput"
              :placeholder="$t('demographics.pleaseSelect')"
            />
          </el-row>

          <el-button
            class="addDegree"
            @click="response.degree_list.push({degree: null, major: null, other: null})"
          >
            {{ $t('demographics.degree.addNew') }}
          </el-button>
        </div>

        <div class="question">
          3. {{ $t('demographics.coachingExp.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="coaching_exp">
            <el-select
              v-model="response.coaching_exp"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
            >
              <el-option
                v-for="item in experienceList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          4. {{ $t('demographics.coachingCertification.question') }}
        </div>

        <div class="response">
          <el-form-item prop="coaching_certifications">
            <el-select
              v-model="response.coaching_certifications"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
              multiple
            >
              <el-option
                v-for="item in certificationList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          5. {{ $t('demographics.coachingCertification.ifOther') }}*
        </div>

        <div class="response multipleInput">
          <el-tag
            v-for="(item, key) in response.certificationsOther"
            v-show="hasOtherCertification"
            :key="key"
            closable
            @close="removeOtherCertifications(item)"
          >
            {{ item }}
          </el-tag>

          <el-row :gutter="10">
            <el-col :span="21">
              <el-input
                v-model="inputCertifications"
                response.coaching_certifications
                :disabled="!hasOtherCertification"
                @keyup.native.enter="addOtherCertifications"
              />
            </el-col>
            <el-col :span="3">
              <el-button
                type="primary"
                :disabled="!hasOtherCertification"
                @click="addOtherCertifications"
              >
                {{ $t('common.add') }}
              </el-button>
            </el-col>
          </el-row>
        </div>

        <div class="question">
          6. {{ $t('demographics.corpYearsExp.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="corp_years_exp">
            <el-select
              v-model="response.corp_years_exp"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
            >
              <el-option
                v-for="item in corporateExperienceList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          7. {{ $t('demographics.industries.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="industries">
            <el-select
              v-model="response.industries"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
              multiple
            >
              <el-option
                v-for="item in industriesList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          8. {{ $t('demographics.funcResponsibilities.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="func_responsibilities">
            <el-select
              v-model="response.func_responsibilities"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
              multiple
            >
              <el-option
                v-for="item in functionalAreasList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          9. {{ $t('demographics.highestLvlSeniority.question') }}*
        </div>

        <div class="response">
          <el-form-item prop="highest_lvl_seniority">
            <el-select
              v-model="response.highest_lvl_seniority"
              :placeholder="$t('demographics.pleaseSelect')"
              :loading="processLoadingList.length > 0"
            >
              <el-option
                v-for="item in organizationalLevelList"
                :key="item.key"
                :label="item.value"
                :value="item.key"
              />
            </el-select>
          </el-form-item>
        </div>

        <div class="question">
          10. {{ $t('demographics.largestEmployers.question') }}
        </div>

        <div class="response multipleInput">
          <el-tag
            v-for="(item, key) in response.largest_employers"
            :key="key"
            closable
            @close="removeEmployers(item)"
          >
            {{ item }}
          </el-tag>

          <el-row :gutter="10">
            <el-col :span="21">
              <el-input
                v-model="inputEmployers"
                @keyup.native.enter="addEmployers"
              />
            </el-col>
            <el-col :span="3">
              <el-button
                type="primary"
                @click="addEmployers"
              >
                {{ $t('common.add') }}
              </el-button>
            </el-col>
          </el-row>
        </div>

        <div class="footer">
          <el-button
            type="primary"
            round
            @click="updateDemographics"
          >
            {{ $t('common.submit') }}
          </el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import api from '@/api';

export default {
  data() {
    return {
      hasDemographics: false,
      processLoadingList: [],
      response: {
        highest_degree: null,
        degree_list: [
          {
            degree: null,
            major: null,
            other: null,
          },
        ],
        coaching_exp: null,
        corp_years_exp: null,
        highest_lvl_seniority: null,
        coaching_certifications: [],
        certificationsOther: [],
        industries: [],
        func_responsibilities: [],
        largest_employers: [],
      },
      rules: {
        highest_degree: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        degree_list: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        coaching_exp: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        corp_years_exp: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        highest_lvl_seniority: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        // coaching_certifications: [
        //   { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        // ],
        industries: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        func_responsibilities: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
        largest_employers: [
          { required: true, message: this.$t('demographics.pleaseInput'), trigger: 'change' },
        ],
      },
      inputCertifications: '',
      inputEmployers: '',
      degreesList: [],
      degreeMajorList: [],
      experienceList: [],
      corporateExperienceList: [],
      organizationalLevelList: [],
      certificationList: [],
      industriesList: [],
      functionalAreasList: [],
    };
  },

  computed: {
    responsiveCol() {
      let col = 12;
      if (this.$mq === 'mobile') {
        col = 24;
      }
      return col;
    },

    hasOtherCertification() {
      return this.response.coaching_certifications.includes('other');
    },
  },

  created() {
    this.getDemographics();

    // Get lists
    this.getDegreesList();
    this.getDegreeMajorList();
    this.getExperienceList();
    this.getCorpExperienceList();
    this.getOrganizationalLevelList();
    this.getCertificationList();
    this.getIndustryList();
    this.getFunctionalAreasList();
  },

  methods: {
    async getDegreesList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getDegreeTypes();
        this.degreesList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getDegreeMajorList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getDegreeMajorTypes();
        this.degreeMajorList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getExperienceList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getCoachExpTypes();
        this.experienceList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getCorpExperienceList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getCorpWorkExpTypes();
        this.corporateExperienceList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getOrganizationalLevelList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getOrgLvlTypes();
        this.organizationalLevelList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getCertificationList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getCoachingCertTypes();
        this.certificationList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getIndustryList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getIndustryTypes();
        this.industriesList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getFunctionalAreasList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getFunctionalAreaTypes();
        this.functionalAreasList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    addOtherCertifications() {
      if (this.inputCertifications) {
        this.response.certificationsOther.push(this.inputCertifications);
        this.inputCertifications = '';
      }
    },

    removeOtherCertifications(cert) {
      const index = this.response.certificationsOther.indexOf(cert);
      this.response.certificationsOther.splice(index, 1);
    },

    addEmployers() {
      if (this.inputEmployers) {
        this.response.largest_employers.push(this.inputEmployers);
        this.inputEmployers = '';
      }
    },

    removeEmployers(empl) {
      const index = this.response.largest_employers.indexOf(empl);
      this.response.largest_employers.splice(index, 1);
    },

    async getDemographics() {
      try {
        await api.getCoachDemographic();
        this.hasDemographics = true;
      } catch (err) {
        this.hasDemographics = false;
      }
    },

    async updateDemographics() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          const data = { ...this.response };
          if (this.hasOtherCertification) {
            data.coaching_certifications = [
              ...data.coaching_certifications,
              ...data.certificationsOther,
            ].filter((el) => el !== 'other');
          }
          delete data.certificationsOther;
          console.log(data);

          try {
            if (this.hasDemographics) {
              await api.updateCoachDemographic(data);
            } else {
              await api.createCoachDemographic(data);
            }

            setTimeout(() => {
              this.$router.push({ name: 'dashboard' }).catch((err) => {});
            }, 500);
          } catch (err) {
            if (err.msg) {
              console.log(err.msg);
              this.$message.error(err.msg);
            }
          }
          return true;
        }
        console.log('error submit!!');
        return false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#completeYourRegistration {
  min-height: 100vh;
  padding: 0 5px 20px;

  .logoWrap {
    width: 100%;
    padding: 45px 5% 10px;
    display: block !important;

    .logoCol {
      .logo {
        img {
          width: 110px;
        }
      }
    }

    .sloganCol {
      text-align: right;

      .slogan {
        img {
          width: 180px;
        }
      }
    }

    &.mobileMode {
      padding: 30px 0;

      .logoCol {
        text-align: center;

        .logo {
          img {
            width: 100px !important;
          }
        }
      }
    }

    &.tabletMode {
      padding: 40px 5%;

      .logoCol {
        .logo {
          img {
            width: 100px !important;
          }
        }
      }

      .sloganCol {
        .slogan {
          img {
            width: 170px !important;
          }
        }
      }
    }
  }

  .el-card {
    margin: 50px auto;
    max-width: 765px;

    .title,
    .subTitle,
    .question,
    .response {
      padding: 0 50px;
    }

    .title {
      font-style: normal;
      font-weight: bold;
      font-size: 18px;
      line-height: 22px;
      color: #003865;
    }

    .subTitle {
      margin-top: 23px;
      margin-bottom: 40px;
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 17px;
      color: #000;
    }

    .question {
      font-style: normal;
      font-weight: bold;
      font-size: 14px;
      line-height: 17px;
      color: #808080;
      margin-top: 20px;
    }

    .response {
      margin-top: 18px;

      .el-select {
        width: 100%;
      }

      &.degrees {
        .degreeItem {
          margin-top: 10px;

          .label {
            font-weight: 500;
            font-size: 14px;
            line-height: 17px;
            color: #000;
            padding-top: 10px;
          }

          .otherInput {
            margin-top: 10px;
            width: 71.5%;
            margin-left: 27%;
          }
        }

        .addDegree {
          width: 100%;
          margin-top: 20px;
        }
      }

      &.multipleInput {
        .el-tag {
          margin-right: 10px;
          margin-bottom: 10px;
        }
      }
    }

    .footer {
      text-align: center;
      margin-top: 60px;

      .el-button {
        height: 43px;
        width: 240px;
        background: #29abe2;
      }
    }
  }
}
</style>
